import React, { useCallback, useMemo, useState } from "react";
import { DataTable } from "@/components/ui/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { Fan, FanServices, Review, type Player } from "@/services/bedrock";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge, PlayerCell, ContactCell, Spinner } from "@/components/ui";
import { ReviewListFiltersBar, ReviewRating, ReviewSentimentBadge, ReplyStatus } from "@/components/reviews";
import { MessageSquare, ClipboardList } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { URLS } from "@/utils";
import { useHistory } from "react-router-dom";
import { ReviewSourceIcon } from "../../components/ReviewSourceIcon";
import { useCoachReviewsFilters } from "./CoachReviewsFiltersContext";
import type { CoachReviewsInfiniteQueryResult } from "./useCoachReviewsInfiniteQuery";
import { FanDialog } from "../../Fans/components";
import { ReviewsExportButton } from "./ReviewsExportButton";
import { ReviewsFiltersPopover } from "./ReviewsFiltersPopover";
import {
  PlayerColumnHeader,
  SourceColumnHeader,
  RatingColumnHeader,
  SentimentColumnHeader,
  ReviewerColumnHeader,
  ContentColumnHeader,
  ReplyStatusColumnHeader,
  DateColumnHeader,
} from "./ReviewsColumnHeader";

type ReviewsTableProps = {
  reviewsQuery: CoachReviewsInfiniteQueryResult;
  onRowClick: (review: Review) => void;
};

function ReviewRatingTableCell({ review }: { review: Review }) {
  const { filters } = useCoachReviewsFilters();
  return (
    <div className="flex justify-center whitespace-nowrap">
      <ReviewRating
        review={review}
        fallback="-"
        primaryRatingType={filters.ratingType}
        secondaryRatingLayout="compact"
      />
    </div>
  );
}

export function ReviewsTable({ reviewsQuery, onRowClick }: ReviewsTableProps) {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { filters, searchInput, setSearchInput, activeFilterCount, toQueryParams, clearAllFilters, players } =
    useCoachReviewsFilters();

  const [filterOpen, setFilterOpen] = useState(false);
  const [fanDialogOpen, setFanDialogOpen] = useState(false);
  const [selectedFan, setSelectedFan] = useState<Fan | null>(null);

  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
  }, [clearAllFilters]);

  const {
    reviews,
    totalCount,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isRefreshing,
    isError,
    refetch,
  } = reviewsQuery;

  const hasActiveFilters = activeFilterCount > 0;
  const hasSearch = Boolean(filters.search.trim()) || Boolean(searchInput.trim());
  const hasSearchOrFilters = hasActiveFilters || hasSearch;
  const clearableFilterCount = activeFilterCount + (hasSearch ? 1 : 0);
  const isTrulyEmpty = !isLoading && !isError && reviews.length === 0 && !hasSearchOrFilters;

  const openFanDialog = useCallback((fan: Fan, player?: Fan["player"]) => {
    setSelectedFan({
      ...fan,
      player: fan.player ?? player,
    });
    setFanDialogOpen(true);
  }, []);

  const saveFanMutation = useMutation({
    mutationFn: async (formData: Record<string, unknown>) => {
      if (!selectedFan) throw new Error("No fan selected");
      return FanServices.updateFan(selectedFan.id, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      queryClient.invalidateQueries({ queryKey: ["fans"] });
      setFanDialogOpen(false);
      setSelectedFan(null);
      toast.success("Contact updated");
    },
    onError: () => {
      toast.error("Error updating contact. Please try again.");
    },
  });

  const columns = useMemo<ColumnDef<Review>[]>(
    () => [
      {
        id: "player",
        minSize: 200,
        header: () => <PlayerColumnHeader />,
        cell: ({ row }) => {
          const player = row.original.player;
          if (!player) {
            return <span className="text-slate-400 italic text-sm">Unassigned</span>;
          }
          return (
            <PlayerCell player={player}>
              <PlayerSegmentationBadges player={player} />
            </PlayerCell>
          );
        },
      },
      {
        accessorKey: "displayName",
        minSize: 200,
        header: () => <ReviewerColumnHeader />,
        cell: ({ row }) => {
          const { displayName, email, fan, player } = row.original;

          return (
            <ContactCell
              name={displayName || "Anonymous"}
              email={email}
              onClick={fan ? () => openFanDialog(fan, player) : undefined}
            />
          );
        },
      },
      {
        accessorKey: "sourceSystem",
        size: 160,
        minSize: 160,
        maxSize: 160,
        header: () => <SourceColumnHeader />,
        cell: ({ row }) => {
          const { sourceSystem, link } = row.original;
          const icon = <ReviewSourceIcon sourceSystem={sourceSystem} />;

          return (
            <div className="flex justify-center whitespace-nowrap" onClick={e => e.stopPropagation()}>
              {link ? (
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex transition-opacity hover:opacity-80 [&_span]:cursor-pointer"
                  aria-label="Open review source"
                >
                  {icon}
                </a>
              ) : (
                icon
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "rating",
        size: 160,
        minSize: 160,
        maxSize: 160,
        header: () => <RatingColumnHeader />,
        cell: ({ row }) => <ReviewRatingTableCell review={row.original} />,
      },
      {
        accessorKey: "sentiment",
        size: 160,
        minSize: 160,
        maxSize: 160,
        header: () => <SentimentColumnHeader />,
        cell: ({ row }) => {
          const sentiment = row.original.sentiment;
          if (!sentiment) return "-";
          return (
            <div className="flex justify-center whitespace-nowrap">
              <ReviewSentimentBadge sentiment={sentiment} />
            </div>
          );
        },
      },
      {
        id: "content",
        minSize: 200,
        header: () => <ContentColumnHeader />,
        cell: ({ row }) => {
          const text = row.original.condensedQuote || row.original.content;
          if (!text) return <span className="text-muted-foreground">-</span>;
          return (
            <p className="line-clamp-2 overflow-hidden text-sm leading-relaxed text-muted-foreground" title={text}>
              {text}
            </p>
          );
        },
      },
      {
        accessorKey: "replied",
        size: 160,
        minSize: 160,
        maxSize: 160,
        header: () => <ReplyStatusColumnHeader />,
        cell: ({ row }) => (
          <div className="flex justify-center whitespace-nowrap">
            <ReplyStatus review={row.original} resolveInternalStatus={true} />
          </div>
        ),
      },
      {
        accessorKey: "publishedAt",
        size: 120,
        minSize: 120,
        maxSize: 120,
        header: () => <DateColumnHeader />,
        cell: ({ row }) => {
          const publishedAt = row.original.publishedAt ? new Date(row.original.publishedAt) : null;

          return (
            <span
              title={publishedAt ? format(publishedAt, "MMM d, yyyy h:mm a") : undefined}
              className="whitespace-nowrap"
            >
              {publishedAt ? format(publishedAt, "MMM d, yyyy") : "-"}
            </span>
          );
        },
      },
    ],
    [openFanDialog]
  );

  if (isTrulyEmpty) {
    return <ReviewsEmptyState navigateToForms={() => history.push(URLS.coach.reputation.forms)} />;
  }

  return (
    <div className="flex max-h-[calc(100dvh-100px)] flex-col gap-4">
      <div className="flex shrink-0 flex-nowrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <ReviewListFiltersBar
            className="w-full min-w-0 md:max-w-96"
            searchValue={searchInput}
            onSearchChange={setSearchInput}
            activeFilterCount={activeFilterCount}
            filterOpen={filterOpen}
            onFilterOpenChange={setFilterOpen}
            filterLabel="Filters"
            filterContent={
              <ReviewsFiltersPopover
                onClose={() => setFilterOpen(false)}
                side="bottom"
                resultCount={totalCount ?? null}
              />
            }
          />
          {hasSearchOrFilters ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 shrink-0 text-slate-500 hover:text-slate-800"
              onClick={handleClearAllFilters}
            >
              Clear all filters ({clearableFilterCount})
            </Button>
          ) : null}

          {isFetching && !isFetchingNextPage && (
            <Spinner className="size-4 text-slate-500 animate-spin shrink-0 ml-1" />
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <ReviewsExportButton
            toQueryParams={toQueryParams}
            playerLabel={players.value?.label ?? null}
            totalCount={totalCount}
            loading={isRefreshing}
            disabled={!totalCount}
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={reviews}
        loading={isLoading && reviews.length === 0}
        disabled={isRefreshing}
        scrollable
        infiniteScroll={{
          hasNextPage: Boolean(hasNextPage),
          isFetchingNextPage,
          fetchNextPage: () => {
            void fetchNextPage();
          },
        }}
        onRowClick={row => onRowClick(row.original)}
        className="h-auto min-h-0 flex-1 [&_table]:min-w-356 [&_table]:table-fixed [&_tbody_tr]:min-h-[60px]"
        emptyState={
          isError ? (
            <ReviewsLoadErrorState onRetry={() => void refetch()} />
          ) : (
            <ReviewsSearchEmptyState
              searchInput={searchInput}
              hasFilters={hasActiveFilters}
              onClear={handleClearAllFilters}
            />
          )
        }
      />

      <FanDialog
        open={fanDialogOpen}
        onOpenChange={open => {
          setFanDialogOpen(open);
          if (!open) setSelectedFan(null);
        }}
        selectedFan={selectedFan}
        onFormSubmit={formData => saveFanMutation.mutate(formData)}
        isSubmitting={saveFanMutation.isPending}
      />
    </div>
  );
}

function ReviewsEmptyState({ navigateToForms }: { navigateToForms: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border p-12 text-center bg-card shadow-sm mt-4">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6 text-primary">
        <MessageSquare className="h-10 w-10" />
      </div>
      <h3 className="text-2xl font-bold tracking-tight mb-2">Monitor Your Reputation</h3>
      <p className="text-muted-foreground max-w-md mx-auto mb-8">
        Track and manage reviews from Google, Facebook, Zillow, and your own custom forms. Analyze sentiment and share
        your best feedback to social media.
      </p>
      <div>
        <Button size="lg" onClick={navigateToForms}>
          <ClipboardList className="mr-2 h-5 w-5" />
          Create feedback form
        </Button>
      </div>
    </div>
  );
}

function ReviewsLoadErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
      <MessageSquare className="h-8 w-8 text-slate-300 mb-3" />
      <h3 className="text-lg font-semibold tracking-tight mb-1">Couldn't load reviews</h3>
      <p className="text-sm text-muted-foreground max-w-md mb-4">Something went wrong. Please try again.</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

function ReviewsSearchEmptyState({
  searchInput,
  hasFilters,
  onClear,
}: {
  searchInput: string;
  hasFilters: boolean;
  onClear: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
      <MessageSquare className="h-8 w-8 text-slate-300 mb-3" />
      <h3 className="text-lg font-semibold tracking-tight mb-1">No reviews found</h3>
      <p className="text-sm text-muted-foreground max-w-md mb-4">
        {searchInput
          ? `We couldn't find any reviews matching "${searchInput}". Try adjusting your spelling, keywords, or filters.`
          : hasFilters
          ? "No reviews match the selected filters. Try clearing filters to see more results."
          : "No reviews matched your criteria."}
      </p>
      {(searchInput || hasFilters) && (
        <Button variant="outline" size="sm" onClick={onClear}>
          Clear filters
        </Button>
      )}
    </div>
  );
}

function PlayerSegmentationBadges({ player }: { player: Player }) {
  const regions = player.regionNames?.filter(Boolean) ?? [];
  const branches = player.branchNames?.filter(Boolean) ?? [];
  if (!regions.length && !branches.length) return null;

  return (
    <div className="mt-0.5 flex flex-row flex-wrap gap-1">
      {regions.map(name => (
        <Badge
          key={`region-${name}`}
          size="small"
          variant="outline"
          className="bg-pink-100 text-[10px] py-0 px-1.5 h-5 font-medium"
        >
          {name}
        </Badge>
      ))}
      {branches.map(name => (
        <Badge
          key={`branch-${name}`}
          size="small"
          variant="outline"
          className="bg-blue-100 text-[10px] py-0 px-1.5 h-5 font-medium"
        >
          {name}
        </Badge>
      ))}
    </div>
  );
}
